import { Price } from "@/design-system/Price/Price";
import { useAccelerateLabel } from "@/hooks/useAccelerateLabel";
import { useFootLabel } from "@/hooks/useFootLabel";
import { cn } from "@/lib/utils";
import { bodyTypeIdToName } from "@/redux/squadBuilder/squadBuilderUtils";
import type { ComparisonCategoryKey } from "@repo/shared/api";
import { strictWinners } from "@repo/shared/hooks";
import { useTranslation } from "@repo/shared/i18n";
import { comparePlayerKey } from "@repo/shared/redux";
import { DetailedPlayer } from "@repo/shared/types";
import {
  calculateAccelerate,
  formatHeight,
  formatWeight,
} from "@repo/shared/utils";

import {
  CompareRow,
  CompareSectionShell,
  compareValueClass,
} from "./CompareSectionShell";
import { Mode } from "./ModeToggle";

// AcceleRATE needs the in-game agility/acceleration/strength plus height —
// detailed-card fields, so it stays a dash until the comparison fetch has
// enriched the player. Stats cap at 99, mirroring the DetailedPlayer calc.
const accelerateFor = (player: DetailedPlayer): string | undefined => {
  const agility = player.inGameAttributes?.["agility"];
  const acceleration = player.inGameAttributes?.["acceleration"];
  const strength = player.inGameAttributes?.["strength"];
  if (
    agility == null ||
    acceleration == null ||
    strength == null ||
    player.height == null
  ) {
    return undefined;
  }
  return calculateAccelerate(
    Math.min(agility, 99),
    Math.min(acceleration, 99),
    Math.min(strength, 99),
    player.height,
    player.gender ?? "MALE",
  );
};

type InfoRow = {
  label: string;
  values: (string | number | undefined | React.ReactNode)[];
  /** Winning column indexes — empty when the row doesn't discriminate. */
  winners?: Set<number>;
};

/**
 * Row won by the highest raw value. Only for stats that are better-is-better
 * whatever the role — a 5* weak foot beats a 3* for a Poacher and a Defender
 * alike — so, unlike the endpoint-scored rows, this needs no comparability
 * check. Winners only when the row discriminates (see `strictWinners`): a
 * lone player, or every column tied, highlights nothing.
 */
const numericRow = (
  label: string,
  values: (number | undefined)[],
): InfoRow => ({
  label,
  values,
  winners: strictWinners(values),
});

/**
 * "Info" section: per-player quick facts read straight off the player objects.
 * Unknown values render a dash.
 *
 * Two kinds of winner, because "better" means different things per row. Weak
 * Foot / Skill Moves are won on the raw value, across roles. AcceleRATE, Body
 * Type, Height and Preferred Foot only mean something relative to a role —
 * Lengthy is a gift to a CB and dead weight on a Poacher — so they're won on
 * the endpoint's category score via `categoryWinners`, which stays empty
 * unless every column shares a role and the solver actually scored the
 * category. Weight has no category, so it never highlights.
 */
export const InfoCompareSection = ({
  players,
  gridStyle,
  categoryWinners,
  open,
  onToggle,
  mode,
  pinnedWidth,
}: {
  players: DetailedPlayer[];
  gridStyle: React.CSSProperties;
  /** Winning column indexes for an endpoint category (see useCompareData). */
  categoryWinners: (key: ComparisonCategoryKey) => Set<number>;
  open: boolean;
  onToggle: () => void;
  mode: Mode;
  /** Mobile: sticky-left width for the section title in the shared scroller. */
  pinnedWidth?: number;
}) => {
  const { t } = useTranslation("compare");
  const accelerateLabel = useAccelerateLabel();
  const footLabel = useFootLabel();
  const rows: InfoRow[] = [
    {
      label: t("info.price"),
      values: players.map((p) => (
        <Price
          size="small"
          priceInfo={p.priceInfo}
          objectiveDisplay={p.objectiveDisplay}
        />
      )),
    },
    {
      // `positions` can be missing at runtime despite the type — staged
      // players from some trigger sites arrive without it.
      label: t("info.positions"),
      values: players.map((p) =>
        p.positions?.length ? p.positions.join(", ") : undefined,
      ),
    },
    numericRow(
      t("info.weakFoot"),
      players.map((p) => p.weakFoot),
    ),
    numericRow(
      t("info.skillMoves"),
      players.map((p) => p.skillMoves),
    ),
    {
      label: t("info.accelerate"),
      values: players.map((p) => accelerateLabel(accelerateFor(p))),
      winners: categoryWinners("accelerate"),
    },
    {
      label: t("info.bodyType"),
      values: players.map((p) =>
        p.bodyTypeId != null ? bodyTypeIdToName[p.bodyTypeId]?.[0] : undefined,
      ),
      winners: categoryWinners("bodyType"),
    },
    {
      label: t("info.height"),
      values: players.map((p) => formatHeight(p.height)),
      winners: categoryWinners("height"),
    },
    {
      label: t("info.weight"),
      values: players.map((p) => formatWeight(p.weight)),
    },
    {
      // The endpoint's category for this row is `correctFoot`.
      label: t("info.preferredFoot"),
      values: players.map((p) => footLabel(p.preferredFoot)),
      winners: categoryWinners("correctFoot"),
    },
  ];

  return (
    <CompareSectionShell
      title={t("sections.info")}
      count={players.length}
      gridStyle={gridStyle}
      open={open}
      onToggle={onToggle}
      mode={mode}
      pinnedWidth={pinnedWidth}
    >
      {rows.map((row, rowIndex) => (
        <CompareRow key={row.label} index={rowIndex} gridStyle={gridStyle}>
          {row.values.map((value, idx) => (
            <div
              key={players[idx] ? comparePlayerKey(players[idx]) : idx}
              className={cn(
                "pl-[30px]",
                // Grouped/compact: label + value share one line (label
                // left, value right); list stacks them.
                mode === "list"
                  ? "flex flex-col gap-[2px]"
                  : "flex items-center justify-between gap-2 pr-[14px]",
              )}
            >
              <span className="text-xs font-medium text-neutral-500">
                {row.label}
              </span>
              <span
                className={cn(
                  "text-xs",
                  compareValueClass(value, row.winners?.has(idx) ?? false),
                )}
              >
                {value ?? "–"}
              </span>
            </div>
          ))}
        </CompareRow>
      ))}
    </CompareSectionShell>
  );
};
